import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Chip, List, ListItem, ListItemAvatar, Avatar, ListItemText, Typography, MenuItem } from '@mui/material';
import { 
  Description as DescriptionIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Folder as FolderIcon,
  InsertDriveFile as InsertDriveFileIcon,
  Article as ArticleIcon,
  Slideshow as SlideshowIcon,
  TableChart as TableChartIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import { tokens } from '../../../theme';
import { alpha } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RoleChip from '../../../SystemDesign/RoleChip';
import CardMenuActions from '../../../SystemDesign/CardMenuActions';
import DownloadPracticalEvidence from './DownloadPracticalEvidence';
import ApprovePracticalEvidence from './ApprovePracticalEvidence';
import RejectPracticalEvidence from './RejectPracticalEvidence';
import UpdatePracticleEvidence from './UpdatePracticleEvidence';
import DeletePractileEvidence from './DeletePractileEvidence';
import * as actions from '../../../Reducers/Actions';

function useFileIconHelpers() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const iconMap = {
    pdf: <DescriptionIcon fontSize="small" />,
    image: <ImageIcon fontSize="small" />,
    video: <VideoIcon fontSize="small" />,
    folder: <FolderIcon fontSize="small" />,
    docx: <ArticleIcon fontSize="small" />,
    pptx: <SlideshowIcon fontSize="small" />,
    xlsx: <TableChartIcon fontSize="small" />,
    zip: <ArchiveIcon fontSize="small" />,
    default: <InsertDriveFileIcon fontSize="small" />,
  };
  const getTypeColor = (type) => {
    switch (type) {
      case 'pdf': return colors.redAccent[400];
      case 'image': return colors.Blue[400];
      case 'video': return colors.purple[400];
      case 'folder': return colors.Orange[400];
      case 'docx': return colors.Blue[400];
      case 'pptx': return colors.Orange[400];
      case 'xlsx': return colors.green[400];
      case 'zip': return colors.grey[500];
      default: return colors.grey[500];
    }
  };
  const getIcon = (type) => iconMap[type] || iconMap.default;
  const deriveType = (ev) => {
    const t = String(ev?.evidence_type || '').toLowerCase();
    const name = String(ev?.file_name || ev?.evidence_url || '').toLowerCase();
    const ext = name.includes('.') ? name.split('.').pop() : '';
    if (t.startsWith('image')) return 'image';
    if (t.startsWith('video')) return 'video';
    // document subtypes by extension
    if (['pdf'].includes(ext)) return 'pdf';
    if (['doc', 'docx'].includes(ext)) return 'docx';
    if (['ppt', 'pptx'].includes(ext)) return 'pptx';
    if (['xls', 'xlsx'].includes(ext)) return 'xlsx';
    if (['zip', 'rar', '7z'].includes(ext)) return 'zip';
    if (t === 'document' || t === 'checklist' || t === 'simulation') return 'pdf'; // fallback doc look
    return ext ? 'default' : (t || 'default');
  };
  return { getTypeColor, getIcon, deriveType, colors };
}

function StatusChip({ status }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const s = String(status || 'pending').toLowerCase();
  const label = s === 'approved' ? 'Approved' : (s === 'rejected' ? 'Rejected' : 'Pending');
  const colorOverride = s === 'approved' ? colors.green[700] : (s === 'rejected' ? colors.redAccent[600] : colors.Orange[400]);
  const bgColorOverride = s === 'approved' ? colors.green[100] : (s === 'rejected' ? colors.redAccent[100] : colors.Orange[100]);
  return (
    <RoleChip role={label} colorOverride={colorOverride} bgColorOverride={bgColorOverride} />
  );
}

export default function PracticalEvidenceList({ evaluationId, evidences: evidencesProp }) {
  const { getTypeColor, getIcon, deriveType } = useFileIconHelpers();
  const dispatch = useDispatch();
  const slice = useSelector((state) => state?.practicalEvidences);
  const reduxEvidences = Array.isArray(slice) ? slice : (slice?.items || []);
  // Initialize reducer from incoming props (like RolesList)
  const initializedRef = React.useRef({}); // track per evaluation
  React.useEffect(() => {
    const key = String(evaluationId);
    if (!initializedRef.current[key] && Array.isArray(evidencesProp) && evidencesProp.length > 0) {
      dispatch({ type: actions.INITIALIZE_PRACTICAL_EVIDENCES, payload: evidencesProp });
      initializedRef.current[key] = true;
    }
  }, [dispatch, evidencesProp, evaluationId]);
  const reduxList = React.useMemo(() => {
    const arr = Array.isArray(reduxEvidences) ? reduxEvidences : (reduxEvidences?.items || []);
    const filtered = arr.filter((e) => String(e?.evaluation_id ?? e?.evaluationId ?? '') === String(evaluationId));
    return filtered;
  }, [reduxEvidences, evaluationId]);
  // Redux is the source of truth. Always render from reduxList for consistency.
  const list = React.useMemo(() => {
    return reduxList;
  }, [reduxList]);

  const handleDownload = (ev) => {
    const url = ev?.evidence_url;
    if (url) {
      window.open(url, '_blank', 'noopener');
      return;
    }
    const id = ev?.evidence_id || ev?.id;
    if (id) {
      const href = `/skills-management/practical-evidences/${id}/download`;
      window.open(href, '_blank', 'noopener');
    }
  };

  if (!evaluationId) return null;

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Evidence files</Typography>
      {list.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No evidence uploaded.</Typography>
      ) : (
        <List dense disablePadding>
          {list.map((ev) => {
            const key = ev.evidence_id || ev.id;
            const name = ev.original_name
              || ev.client_name
              || ev.file_name
              || (ev.evidence_url ? ev.evidence_url.split('/').pop() : 'Evidence');
            return (
              <ListItem key={key} sx={{ mb: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                secondaryAction={
                  <CardMenuActions icon={<MoreVertIcon />}>
                    {/* Approve action */}
                    <ApprovePracticalEvidence evidenceId={key} /> 
                    <RejectPracticalEvidence evidenceId={key} />
                    <UpdatePracticleEvidence row={{ original: ev }} />
                    <DeletePractileEvidence row={{ original: ev }} />
                    <DownloadPracticalEvidence evidenceId={key} fileName={name} />
                  </CardMenuActions>
                }
              >
                <ListItemAvatar>
                  {(() => {
                    const type = deriveType(ev);
                    const color = getTypeColor(type);
                    return (
                      <Avatar
                        variant="rounded"
                        sx={{ bgcolor: alpha(color, 0.1), color }}
                      >
                        {getIcon(type)}
                      </Avatar>
                    );
                  })()}
                </ListItemAvatar>
                <ListItemText
                  primary={name}
                  secondary={<StatusChip status={ev.status} />}
                  secondaryTypographyProps={{ component: 'div' }}
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
}
